<template>
  <div class="text-sm gap-10 mt-10 m-auto px-0">
    <div class="max-w-screen-xl m-auto px-4">
      <UserProfileHeader
        :image-url="supplierGetters.getLogo(partnerData)"
        :name="
          supplierGetters.isNameLikePageTitle(partnerData)
            ? $t('supplierProfile.heading', {
                name: supplierGetters.getPageTitle(partnerData),
              })
            : supplierGetters.getPageTitle(partnerData)
        "
        :subtitle="supplierGetters.getReviewsAverage(partnerData)"
        class="my-5"
        :class="!supplierGetters.getAddress(partnerData) ? 'mb-4' : ''"
        :title-level="1"
        :title-h-class="'text-h4'"
        large-image
      />

      <LocationInfo
        v-if="supplierGetters.getAddress(partnerData)"
        :title="
          $t('supplierProfile.address.title', {
            name: supplierGetters.getName(partnerData),
          })
        "
        :subtitle="supplierGetters.getAddress(partnerData)"
        class="supplier-page-profile-info__location-info align-items-start"
      />

      <div
        ref="eventsSection"
        class="scroll-mt-[115px] md:scroll-mt-[var(--kft-navbar-height)]"
      >
        <ProductShowcase
          class="mt-5"
          :products="partnerProducts"
          :tags="[]"
          :title="
            $t('supplierProfile.eventsFromTheSameSupplier', {
              supplierName: supplierGetters.getName(partnerData),
            })
          "
          :title-h-class="'text-h4'"
          :title-level="3"
          :algolia-insights-client="algoliaInsightsClient"
          algolia-insights-label="Supplier Profile Page"
          :refresh-on-client-side="false"
          image-loading="eager"
        />

        <FilterPagination
          v-if="totalEvents > EVENTS_PER_PAGE"
          :hits-per-page="EVENTS_PER_PAGE"
          :nb-hits="totalEvents"
          :current-page="currentPage"
          class="mb-6"
          @updated="onPageChange"
        />
      </div>
    </div>
    <div
      v-if="supplierGetters.getReviewsList(partnerData).length > 0"
      class="my-10 py-5 md:py-10 bg-[#F9F7F5]"
    >
      <LazyReviewShowcase
        v-if="supplierGetters.getReviewsTotal(partnerData)"
        :average-rating="supplierGetters.getReviewsAverage(partnerData)"
        :review-count="supplierGetters.getReviewsTotal(partnerData)"
        :supplier-name="supplierGetters.getName(partnerData)"
        :reviews="supplierGetters.getReviewsList(partnerData)"
        :supplier-created-at="supplierGetters.getCreatedAt(partnerData)"
        :display-item-title="true"
      />
    </div>
    <div class="max-w-screen-xl m-auto px-4">
      <Heading
        :level="2"
        :h-class="'text-h4'"
        :title="
          $t('supplierProfile.supplierDescriptionTitle', {
            supplierName: supplierGetters.getName(partnerData),
          })
        "
        class="kft-heading--left mb-4 mt-8"
      />

      <StrapiContentWrapper
        class="w-full mt-5 mb-4"
        :rich-text="supplierGetters.getDescription(partnerData)"
      />

      <PartnerRelatedLinks
        v-if="!isSubdomain"
        :categories-list="categoryList || []"
        :supplier-name="supplierGetters.getName(partnerData)"
        :supplier-id="supplierGetters.getId(partnerData)"
        :supplier-localities="supplierLocalitiesWithPage || []"
        class="supplier-profile__related-links"
      />

      <PartnerArticles
        v-if="!isSubdomain"
        :articles="articles"
        :partner-name="supplierGetters.getName(partnerData)"
        class="supplier-profile__articles my-4 mb-0"
      />
    </div>
  </div>
</template>
<script setup lang="ts">
import * as qs from 'qs'
import useEventSchema from '~/composables/schemaOrg/useEventSchema'
import usePartnerSchema from '~/composables/schemaOrg/usePartnerSchema'
import type { KftApiDefaultResponse } from '~/composables/types/kft-api'
import type { Supplier } from '~/composables/types/event'
import { enrichAndParseSupplierEvents } from '~/utils/helpers'
import { mergeKftEventDescriptionSettingsIncludes } from '~/utils/kftEventDescriptionIncludes'
import { useIsSubdomain } from '~/composables/useSubdomain'
import { getStrapiPartnerArticlesUrl } from '~/utils/strapiURLhelpers'

definePageMeta({
  layout: 'default-max-screen',
  contentGroup: 'partner',
  allowOnPartnerPage: true,
  validate: (route) => {
    return !route.params?.partnerPermalink.includes('konfetti-gutscheine')
  },
})
const route = useRoute()
const { locale, t: $t } = useI18n()
const isSubdomain = useIsSubdomain()
const { client: algoliaInsightsClient } = useAlgoliaSearchInsights()
const partnerPermalink = route.params.partnerPermalink

const params2 = qs.stringify(
  { include: ['reviews', 'logo'] },
  {
    encodeValuesOnly: true, // prettify URL
    arrayFormat: 'comma',
  }
)
const { data: partnerResponse = undefined } = await useKftApiFetch<
  KftApiDefaultResponse<Supplier>
>(`v1/store/suppliers/${partnerPermalink}?${params2}`)

const partnerData: Supplier | undefined = partnerResponse?.value?.data

if (!partnerData) {
  throw createError({
    statusCode: 404,
    statusMessage: 'Page Not Found',
  })
}

const partnerID = computed(() => {
  return String(supplierGetters.getId(partnerData || {}))
})

const EVENTS_PER_PAGE = 20
const currentPage = ref(1)
const eventsSection = ref<HTMLElement | null>(null)

interface PartnerEventsResponse {
  data: any[]
  meta: {
    pagination: { total: number; total_pages: number; current_page: number }
  }
}

const config = useRuntimeConfig()
const localeBCP47 = convertLocaleIntoBCP47(locale.value)
const localeISO = convertISO2Locale(locale.value)

const buildEventsUrl = (page: number) => {
  const paramsObj = {
    limit: EVENTS_PER_PAGE,
    page,
    include: mergeKftEventDescriptionSettingsIncludes([
      'categories',
      'address.locality',
      'supplier',
    ]),
    search: `supplier_id:${partnerID.value};hasEvent:false;${isSubdomain.value ? 'embeddedPublished:1' : 'konfettiPublished:1'}`,
  }
  return `v1/store/events?${qs.stringify(paramsObj, { encode: false, arrayFormat: 'comma' })}`
}

const { data: partnerProductsResponse } = await useFetch<PartnerEventsResponse>(
  () => buildEventsUrl(currentPage.value),
  {
    baseURL: config.public.apiBaseURL,
    headers: {
      'Content-Type': 'application/json',
      'Accept-Language': `${localeBCP47},${localeISO}`,
    },
    default: (): PartnerEventsResponse => ({
      data: [],
      meta: { pagination: { total: 0, total_pages: 1, current_page: 1 } },
    }),
  }
)

const partnerProducts = computed(() => {
  const products = partnerProductsResponse.value?.data
  if (!products?.length || !partnerData) return []
  return enrichAndParseSupplierEvents(products, partnerData)
})

const totalEvents = computed(
  () => partnerProductsResponse.value?.meta?.pagination?.total ?? 0
)

const onPageChange = (page: number) => {
  currentPage.value = page
  nextTick(() => {
    eventsSection.value?.scrollIntoView({ behavior: 'smooth', block: 'start' })
  })
}

const { data: supplierLocalities = [] } = await useKftApiFetch(
  `v1/store/suppliers/${partnerID.value}/localities`,
  {
    transform: (data) => data?.data || {},
  }
)

const supplierLocalitiesWithPage = computed(() =>
  supplierLocalities.value?.filter((locality) => !!locality?.has_page)
)

const categoryList = computed(() => {
  if (!partnerProducts.value) return []

  // Get all categories
  const allCategories = partnerProducts.value.flatMap((product) => {
    return eventGetters.getCategories(product)
  })

  // Return unique categories
  return allCategories.reduce((acc, category) => {
    if (category.slug && !acc.some((c) => c.slug === category.slug)) {
      acc.push(category)
    }
    return acc
  }, [])
})

const { data: articles } = await useStrapiSingleType(
  `/${getStrapiPartnerArticlesUrl(partnerID.value, locale.value)}`,
  {
    key: 'articles-partner-' + partnerID.value,
  }
)
const translationPayload = {
  supplierName: supplierGetters.getName(partnerData),
  supplierUserName: supplierGetters.getUserName(partnerData),
}
const getPageTitle = () => {
  if (supplierGetters.isNameLikePageTitle(partnerData)) {
    return $t('general.titles.supplier-profile')
      ? $t('general.titles.supplier-profile', translationPayload)
      : null
  } else {
    return supplierGetters.getPageTitle(partnerData)
  }
}
const description = $t(
  'general.descriptions.supplier-profile',
  translationPayload
)
const { script: eventSchemas } = useEventSchema(
  partnerProductsResponse.value?.data ?? []
)
const { script: partnerSchema } = usePartnerSchema(
  partnerData,
  partnerProductsResponse.value?.data ?? []
)
const seoOptions = computed(() => ({
  shouldBeIndexed: true,
  meta: {
    title: `${getPageTitle()} | ${$t('general.titles.base')}`,
    'og:title': `${getPageTitle()} | ${$t('general.titles.base')}`,
    description,
    'og:description': description,
    ...(supplierGetters.getOpenGraphLogo(partnerData) && {
      'og:image': supplierGetters.getOpenGraphLogo(partnerData),
    }),
  },
  head: {
    title: getPageTitle(),
    script: [eventSchemas, partnerSchema],
  },
}))
useSeoHelper(seoOptions)
</script>
